import { useState, useEffect, Dispatch, SetStateAction } from 'react'

interface UseValueEditorParams<ExternalValue, InternalValue> {
  value: ExternalValue
  parseValue: (val: ExternalValue) => InternalValue
  serializeValue: (val: InternalValue) => ExternalValue
  onChange?: (data: { value: ExternalValue }) => void
  onSave?: (val: ExternalValue) => void
  onEditCancel?: () => void
}

interface UseValueEditorReturn<InternalValue, _ExternalValue> {
  localValue: InternalValue
  setLocalValue: Dispatch<SetStateAction<InternalValue>>
  handleLocalChange: (newValue: InternalValue) => void
  handleBlurOrSave: () => void
}

function useValueEditor<ExternalValue, InternalValue>({
  value,
  parseValue,
  serializeValue,
  onChange,
  onSave,
  onEditCancel,
}: UseValueEditorParams<ExternalValue, InternalValue>): UseValueEditorReturn<InternalValue, ExternalValue> {
  const [localValue, setLocalValue] = useState<InternalValue>(parseValue(value))

  useEffect(() => {
    setLocalValue(parseValue(value))
  }, [value, parseValue])

  const handleLocalChange = (newValue: InternalValue) => {
    setLocalValue(newValue)
    if (onChange) {
      onChange({ value: serializeValue(newValue) })
    }
  }

  const handleBlurOrSave = () => {
    if (onSave) {
      onSave(serializeValue(localValue))
    } else if (onEditCancel) {
      onEditCancel()
    }
  }

  return { localValue, setLocalValue, handleLocalChange, handleBlurOrSave }
}

export default useValueEditor
